import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Space, Button, Spinner, Row } from "@masterteam/mt-design-react";

import AdminPage from "../../AdminPage";
import AdminSearch from "../../AdminSearch";
import AddGroup from "./AddGroup";
import DeleteModal from "../../DeleteModal";
import Table from "../../NewTable";

import { ReactComponent as PlusIcon } from "../../../assets/images/plus-symbol.svg";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete.svg";

import api from "../../../providers/api";
import { addToast } from "../../../helpers";

import "../Users/style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

function Groups() {
  const intl = useIntl();
  const [searchedFilter, setSearchedFilter] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupModal, setGroupModal] = useState(initialModalState);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [deleteLoading, setDeleteLoading] = useState(false);

  function getGroups() {
    setLoading(true);
    api.group
      .getAll()
      .then((res) => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setLoading(false);
          setGroups(Data);
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      });
  }

  function selectGroup(group, action) {
    switch (action) {
      case "delete":
        setDeleteModal({
          isOpen: true,
          data: group.ID,
        });
        break;

      case "edit":
        setGroupModal({
          isOpen: true,
          data: group,
        });
        break;

      default:
        break;
    }
  }

  function deleteGroup(id) {
    setDeleteLoading(true);
    api.group
      .delete(id)
      .then((res) => {
        const { StatusCode } = res;
        if (StatusCode === "Success") {
          addToast.success("user-management.group-deleted-successfuly");
          setDeleteModal({
            isOpen: false,
            data: null,
          });
          setDeleteLoading(false);
          setGroups((currentGroups) =>
            currentGroups.filter((item) => item.ID !== id)
          );
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "delete-user-error",
        });
        setDeleteModal({
          isOpen: false,
          data: null,
        });
        setGroupModal({
          isOpen: true,
          data: null,
        });
      });
  }

  useEffect(() => {
    getGroups();
  }, []);

  function showAddGroupModal() {
    setGroupModal({
      isOpen: true,
      data: null,
    });
  }

  function updateGroupsList(Data, isEdit) {
    if (Data) {
      let _groups = [...groups];

      if (!isEdit) {
        _groups.push(Data);
      } else {
        const index = _groups.findIndex((item) => item.ID === Data.ID);
        if (index !== -1) {
          _groups[index] = Data;
        }
      }
      setGroupModal(initialModalState);
      setLoading(false);
      setGroups(_groups);
    } else {
      setGroupModal(initialModalState);
      setLoading(false);
    }
  }

  const columns = [
    {
      key: "photo",
      cell: "Title",
      header: intl.formatMessage({ id: "group.group-name" }),
      width: "75%",
    },
    {
      key: "users",
      cell: "UsersCount",
      header: intl.formatMessage({ id: "group.number-users" }),
      width: "15%",
    },
  ];

  const actions = [
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.update" }),
      onClick: (item) => {
        selectGroup(item, "edit");
      },
    },
    {
      key: "delete",
      variant: "error",
      Icon: DeleteIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.delete" }),
      onClick: (item) => {
        selectGroup(item, "delete");
      },
      hidden: (item) => !item.IsDeletable,
      disabled: (item) => !item.IsDeletable,
    },
  ];

  return (
    <AdminPage
      title={intl.formatMessage({ id: "common.groups" })}
      loading={loading}
    >
      <div className="users-container">
        <div className="animated fadeInLeft">
          <div className="users-header">
            <Space align="center">
              <Button onClick={showAddGroupModal} Icon={PlusIcon}>
                {intl.formatMessage({ id: "common.add" })}
              </Button>
            </Space>
            <div className="d-flex search-users-container">
              <span className="users-count-label">
                {intl.formatMessage({ id: "common.groups" })}:&nbsp;
              </span>
              <span
                className="users-count"
                style={{ color: "var(--primary-color)" }}
              >
                {groups.length}
              </span>
              <AdminSearch
                onChange={(event) => {
                  setSearchedFilter(event);
                }}
              />
            </div>
          </div>
          {groupModal.isOpen ? (
            <AddGroup
              showModal={groupModal.isOpen}
              editMode={groupModal.data}
              selectedGroup={groupModal.data}
              callback={updateGroupsList}
              closeHandler={() => setGroupModal(initialModalState)}
            />
          ) : null}
          <Table
            data={groups.filter((group) =>
              group.Title?.toLowerCase().includes(
                searchedFilter?.trim().toLowerCase()
              )
            )}
            columns={columns}
            actions={actions}
            actionsHeader={intl.formatMessage({ id: "common.operations" })}
          />
        </div>
        {deleteModal.isOpen ? (
          <DeleteModal
            isOpen={deleteModal.isOpen}
            title={intl.formatMessage({ id: "common.group" })}
            closeModal={() =>
              setDeleteModal({
                isOpen: false,
                data: null,
              })
            }
            handleDelete={() => deleteGroup(deleteModal.data)}
            ButtonStatus={deleteLoading}
          />
        ) : null}
      </div>
    </AdminPage>
  );
}

export default Groups;
